import type { ProfilerInfo } from 'api/ServiceClientImplementation';
import { ServiceClientImplementation } from 'api/ServiceClientImplementation';
import { JsonStorage } from 'ts/commons/JsonStorage';
import { createStore, useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

/** State for global UI debugger profiler management */
type UiDebuggerState = {
	/** Whether profiler recording is enabled */
	enabled: boolean;
	/** Array of profiling information collected */
	profilingInfos: ProfilerInfo[];
	/** Enable profiler recording */
	enable: () => void;
	/** Disable profiler recording */
	disable: () => void;
	/** Switches between enabled/disabled. */
	toggle: () => void;
};

/** Key for storing profiler enablement state in localStorage - reuse existing debugger key */
const DEBUGGER_ENABLED_STORAGE_KEY = 'ui-debugger-enabled';

/** Get initial enabled state from localStorage */
function getInitialEnabledState(): boolean {
	return JsonStorage.LOCAL_STORAGE.getOrElse(DEBUGGER_ENABLED_STORAGE_KEY, false);
}

/** Set enabled state in localStorage */
function setEnabledStateInStorage(enabled: boolean): void {
	JsonStorage.LOCAL_STORAGE.set(DEBUGGER_ENABLED_STORAGE_KEY, enabled);
}

/** Update ServiceClientImplementation profiler hook based on enabled state */
function updateProfilerHook(enabled: boolean, addProfilingInfo: (info: ProfilerInfo) => void): void {
	if (enabled) {
		ServiceClientImplementation.setProfilerHook(addProfilingInfo);
	} else {
		ServiceClientImplementation.setProfilerHook(null);
	}
}

/** Global store for UI debugger profiler state */
const UI_DEBUGGER_STORE = createStore<UiDebuggerState>((set, get) => {
	const initialEnabled = getInitialEnabledState();

	function addProfilingInfo(info: ProfilerInfo) {
		set(state => ({
			profilingInfos: [...state.profilingInfos, info]
		}));
	}

	updateProfilerHook(initialEnabled, addProfilingInfo);

	function setEnabled(enabled: boolean) {
		set({ enabled });
		setEnabledStateInStorage(enabled);
		updateProfilerHook(enabled, addProfilingInfo);
	}

	return {
		enabled: initialEnabled,
		profilingInfos: [],
		enable: () => setEnabled(true),
		disable: () => setEnabled(false),
		toggle: () => setEnabled(!get().enabled),
		clearData() {
			set({ profilingInfos: [] });
		}
	};
});

/** Hook to get profiler enabled state */
export function useUiDebuggerEnabled(): boolean {
	return useStore(UI_DEBUGGER_STORE, state => state.enabled);
}

/** Hook to get profiling infos */
export function useUiDebuggerProfilingInfos(): ProfilerInfo[] {
	return useStore(UI_DEBUGGER_STORE, state => state.profilingInfos);
}

/** Hook to get the UI debugger actions */
export function useUiDebuggerActions(): {
	toggle: () => void;
	enable: () => void;
	disable: () => void;
} {
	return useStore(
		UI_DEBUGGER_STORE,
		useShallow(state => ({
			toggle: state.toggle,
			enable: state.enable,
			disable: state.disable
		}))
	);
}
